export const PAIRE_SIZE = 6;
export const PAIRE_CELL_COUNT = 36;
export const PAIRE_SYMBOLS = [1, 2] as const;

export type PaireSymbol = (typeof PAIRE_SYMBOLS)[number];
export type PaireValue = 0 | PaireSymbol;
export type PaireConstraint = { a: number; b: number; kind: 'eq' | 'neq' };

export type PairePuzzle = {
  puzzle: PaireValue[];
  solution: PaireSymbol[];
  constraints: PaireConstraint[];
};

export function isPairePuzzle(value: unknown): value is PairePuzzle {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<PairePuzzle>;
  if (
    !Array.isArray(candidate.puzzle) ||
    candidate.puzzle.length !== PAIRE_CELL_COUNT ||
    !candidate.puzzle.every((cell) => cell === 0 || cell === 1 || cell === 2) ||
    !Array.isArray(candidate.solution) ||
    candidate.solution.length !== PAIRE_CELL_COUNT ||
    !candidate.solution.every((cell) => cell === 1 || cell === 2) ||
    !candidate.puzzle.every((cell, index) => cell === 0 || cell === candidate.solution![index]) ||
    candidate.puzzle.filter(Boolean).length !== 6 ||
    !Array.isArray(candidate.constraints) ||
    candidate.constraints.length !== 12
  ) {
    return false;
  }
  const validSolution = Array.from({ length: PAIRE_SIZE * 2 }, (_, unitIndex) => {
    const unit = Array.from({ length: PAIRE_SIZE }, (_, offset) =>
      unitIndex < PAIRE_SIZE
        ? unitIndex * PAIRE_SIZE + offset
        : offset * PAIRE_SIZE + (unitIndex - PAIRE_SIZE),
    );
    const values = unit.map((index) => candidate.solution![index]);
    return (
      values.filter((value) => value === 1).length === 3 &&
      values.filter((value) => value === 2).length === 3 &&
      !values.some(
        (value, index) =>
          index <= PAIRE_SIZE - 3 && value === values[index + 1] && value === values[index + 2],
      )
    );
  }).every(Boolean);
  if (!validSolution) return false;

  const keys = new Set<string>();
  return candidate.constraints.every((constraint) => {
    if (!constraint || typeof constraint !== 'object') return false;
    const { a, b, kind } = constraint as Partial<PaireConstraint>;
    if (
      !Number.isInteger(a) ||
      !Number.isInteger(b) ||
      a! < 0 ||
      b! < 0 ||
      a! >= PAIRE_CELL_COUNT ||
      b! >= PAIRE_CELL_COUNT ||
      (kind !== 'eq' && kind !== 'neq')
    ) {
      return false;
    }
    const rowA = Math.floor(a! / PAIRE_SIZE);
    const rowB = Math.floor(b! / PAIRE_SIZE);
    const columnA = a! % PAIRE_SIZE;
    const columnB = b! % PAIRE_SIZE;
    if (Math.abs(rowA - rowB) + Math.abs(columnA - columnB) !== 1) return false;
    const key = a! < b! ? `${a}:${b}` : `${b}:${a}`;
    if (keys.has(key)) return false;
    keys.add(key);
    const equal = candidate.solution![a!] === candidate.solution![b!];
    return kind === (equal ? 'eq' : 'neq');
  });
}
